<script setup lang="ts">
import { formatRate, impliedRate, type Money } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import type { FxTransferDraft, FxTransferMatch } from '../composables/use-fx-transfer-extension.ts'
import type { RowView } from '../internal/extension.ts'

const props = defineProps<{ match: FxTransferMatch; draft: FxTransferDraft; view: RowView }>()
const emit = defineEmits<{ 'update:draft': [FxTransferDraft] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const rateLabel = computed(() => {
  const sent = props.view.total
  const rate =
    sent === undefined || props.draft.received === null ? undefined : impliedRate(sent, props.draft.received)
  return rate === undefined ? undefined : formatRate(rate, { locale: currentLocale.value })
})

function update(received: Money | null): void {
  emit('update:draft', { received })
}
</script>

<template>
  <div class="flex flex-wrap items-center gap-[var(--space-cell)]">
    <div
      class="flex items-center gap-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
    >
      <span aria-hidden="true">{{ t('entries.fx.receivedLabel') }}</span>
      <div class="w-40">
        <AmountInput
          :model-value="draft.received"
          :currency="match.receivedCurrency"
          :locale="currentLocale"
          :show-total="false"
          :aria-label="t('entries.fx.receivedAriaLabel', { currency: match.receivedCurrency })"
          @update:model-value="update"
        />
      </div>
    </div>
    <span v-if="rateLabel" class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
      {{ t('entries.fx.impliedRate', { rate: rateLabel }) }}
    </span>
  </div>
</template>
